import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useMediaQuery, useTheme, Tooltip, Checkbox } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import { Box } from "@mui/system";
import ExpandedRowContent from "./ExpandedRowContent";

const ResponsiveTableContainer = styled(TableContainer)(() => ({
  overflow: "auto",
  "&::-webkit-scrollbar": { width: "8px", height: "8px", borderRadius: "10px" },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "#647490", borderRadius: "10px" },
  "&::-webkit-scrollbar-track": { backgroundColor: "#B7BFCC", borderRadius: "10px", margin: "5px" },
  scrollbarWidth: "thin",
  scrollbarColor: "#647490 #B7BFCC",
}));

const TC = styled(TableCell)(() => ({
  padding: "8px 12px",
  minWidth: "90px",
  maxHeight: "50px",
  overflow: "hidden",
}));

const VehicleNameCell = styled(TableCell)(() => ({
  padding: "8px 12px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minWidth: "180px",
  maxHeight: "50px",
  "& .vehicle-image": {
    width: "40px",
    height: "40px",
    minWidth: "40px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #647490",
    flexShrink: 0,
  },
  "& .vehicle-name-text": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "120px",
  },
}));

const SortIcons = styled("div")(() => ({
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  marginLeft: "6px",
  gap: 0,
  verticalAlign: "middle",
}));

const ArrowUp = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><path d="M4 0 L8 6 L0 6 Z" /></svg>
);
const ArrowDown = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><path d="M4 8 L8 2 L0 2 Z" /></svg>
);

/** Table row avatar: real vehicle image or initials (avoids broken external placeholder URLs). */
function TableVehicleAvatar({ src, label }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  const initials = (label || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";
  if (src && !broken) {
    return (
      <img
        src={src}
        alt=""
        className="vehicle-image"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div
      className="vehicle-image"
      role="img"
      aria-label={label || "Vehicle"}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#E5E8EE",
        color: "#647490",
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      {initials}
    </div>
  );
}

const InsideIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M15.8237 1.67676L18.3237 4.17676L16.4178 6.08342L13.9178 3.58342L15.8237 1.67676ZM6.66699 13.3334H9.16699L15.2395 7.26092L12.7395 4.76092L6.66699 10.8334V13.3334Z" fill="#FF6A00" />
    <path d="M15.8333 15.8333H6.79833C6.77667 15.8333 6.75417 15.8417 6.7325 15.8417C6.705 15.8417 6.6775 15.8342 6.64917 15.8333H4.16667V4.16667H9.8725L11.5392 2.5H4.16667C3.2475 2.5 2.5 3.24667 2.5 4.16667V15.8333C2.5 16.7533 3.2475 17.5 4.16667 17.5H15.8333C16.2754 17.5 16.6993 17.3244 17.0118 17.0118C17.3244 16.6993 17.5 16.2754 17.5 15.8333V8.61L15.8333 10.2767V15.8333Z" fill="#FF6A00" />
  </svg>
);

const SORT_NONE = "none", SORT_ASC = "asc", SORT_DESC = "desc";

const getVisiblePaginationPages = (currentPage, totalPages) => {
  if (totalPages <= 3) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 2) return [1, 2, 3];
  if (currentPage >= totalPages - 1) {
    return [totalPages - 2, totalPages - 1, totalPages];
  }

  return [currentPage - 1, currentPage, currentPage + 1];
};

export default function EntryExitTable({
  data = [],
  gates = [],
  loading = false,
  onLogUpdated,
  pagination,
  onPageChange,
  skeletonRowCount = 5,
  selectable = false,
  selectedRowKeys = [],
  onSelectionChange,
  allSelectableKeys = [],
}) {
  const { t } = useTranslation("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: SORT_NONE });
  const [originalData, setOriginalData] = useState([]);
  const tableRef = useRef(null);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const selectableKeys = useMemo(
    () => (allSelectableKeys.length > 0 ? allSelectableKeys : data.map((r) => r.id ?? r._logId)),
    [allSelectableKeys, data]
  );
  const isAllSelected =
    selectableKeys.length > 0 && selectableKeys.every((k) => selectedRowKeys.includes(k));
  const isIndeterminate =
    selectedRowKeys.length > 0 && !isAllSelected && selectableKeys.some((k) => selectedRowKeys.includes(k));

  const handleSelectAll = (e) => {
    if (!onSelectionChange) return;
    if (e.target.checked) {
      onSelectionChange(selectableKeys);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectRow = (key, e) => {
    e.stopPropagation();
    if (!onSelectionChange) return;
    if (selectedRowKeys.includes(key)) {
      onSelectionChange(selectedRowKeys.filter((k) => k !== key));
    } else {
      onSelectionChange([...selectedRowKeys, key]);
    }
  };

  useEffect(() => { setOriginalData([...data]); }, [data]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: SORT_ASC };
      if (prev.direction === SORT_ASC) return { key, direction: SORT_DESC };
      if (prev.direction === SORT_DESC) return { key, direction: SORT_NONE };
      return { key, direction: SORT_ASC };
    });
  };

  const sortedData = useMemo(() => {
    if (sortConfig.direction === SORT_NONE || !sortConfig.key) return originalData;
    return [...originalData].sort((a, b) => {
      let av = String(a[sortConfig.key] ?? "").toLowerCase();
      let bv = String(b[sortConfig.key] ?? "").toLowerCase();
      const an = parseFloat(av), bn = parseFloat(bv);
      if (!isNaN(an) && !isNaN(bn))
        return sortConfig.direction === SORT_ASC ? an - bn : bn - an;
      return sortConfig.direction === SORT_ASC ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [originalData, sortConfig]);

  const renderSortIcons = (key) => {
    const active = sortConfig.key === key ? sortConfig.direction : SORT_NONE;
    return (
      <SortIcons>
        <span style={{ opacity: active === SORT_ASC ? 1 : 0.4 }}><ArrowUp /></span>
        <span style={{ opacity: active === SORT_DESC ? 1 : 0.4 }}><ArrowDown /></span>
      </SortIcons>
    );
  };

  const headerCellBase = {
    color: "#1F242E",
    fontWeight: "bold",
    textAlign: "center",
    borderBottom: "none",
    whiteSpace: "normal",
    padding: "6px 10px",
  };
  const currentPage = Number(pagination?.page) || 1;
  const totalPages = Number(pagination?.totalPages) || 1;
  const paginationPages = getVisiblePaginationPages(currentPage, totalPages);
  const paginationButtonBase = {
    minWidth: 34,
    height: 34,
    padding: "0 10px",
    borderRadius: 6,
    border: "1px solid #C8D0DC",
    background: "#FFFFFF",
    color: "#1F242E",
    fontWeight: 500,
  };

  return (
    <Box
      sx={{
        mt: "20px",
        border: "1px solid #D8DEE8",
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
      }}
    >
      <ResponsiveTableContainer ref={tableRef}>
        <Table sx={{ tableLayout: "auto", width: "100%", minWidth: isSmall ? "100%" : "800px" }}>
          <TableHead>
            {/* Row 1: group headers */}
            <TableRow sx={{ backgroundColor: "#647490" }}>
              {selectable && (
                <TC
                  rowSpan={2}
                  sx={{
                    width: "1%",
                    whiteSpace: "nowrap",
                    padding: "4px 8px",
                    textAlign: "center",
                    backgroundColor: "#647490",
                    borderBottom: "none",
                    borderRight: "1px solid #D8DEE8",
                    verticalAlign: "middle",
                  }}
                >
                  <Checkbox
                    size="small"
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    sx={{
                      color: "#FFFFFF",
                      "&.Mui-checked": { color: "#FFFFFF" },
                      "&.MuiCheckbox-indeterminate": { color: "#FFFFFF" },
                      padding: "2px",
                    }}
                  />
                </TC>
              )}
              <TC sx={{ color: "#fff", fontWeight: 700, minWidth: 180, fontSize: "20px" }}>{t("Vehicle")}</TC>
              <TC sx={{ color: "#fff", fontWeight: 700, minWidth: 120, fontSize: "20px" }}>{t("Reg. No")}</TC>
              <TC sx={{ color: "#fff", fontWeight: 700, minWidth: 80, fontSize: "20px" }}>{t("Type")}</TC>
              <TC
                colSpan={3}
                sx={{
                  backgroundColor: "#C8E6C9", color: "#1B5E20", fontWeight: 700,
                  textAlign: "center", borderLeft: "2px solid #2e7d32", borderRight: "2px solid #2e7d32",
                  fontSize: "16px",
                }}
              >
                {t("Entry Details")}
              </TC>
              <TC
                colSpan={3}
                sx={{
                  backgroundColor: "#FFCDD2", color: "#B71C1C", fontWeight: 700,
                  textAlign: "center", borderLeft: "2px solid #da190b", borderRight: "2px solid #da190b",
                  fontSize: "16px",
                }}
              >
                {t("Exit Details")}
              </TC>
              <TC
                sx={{ color: "#fff", fontWeight: 700, textAlign: "center", cursor: "pointer", minWidth: 90 }}
                onClick={() => handleSort("duration")}
              >
                {t("Duration")} {renderSortIcons("duration")}
              </TC>
            </TableRow>

            {/* Row 2: sub-column headers */}
            <TableRow>
              <TC sx={{ backgroundColor: "#647490", borderBottom: "none" }} />
              <TC sx={{ backgroundColor: "#647490", borderBottom: "none" }} />
              <TC sx={{ backgroundColor: "#647490", borderBottom: "none" }} />
              {/* Entry sub-headers */}
              {["Date", "Time", "Gate"].map((label, i) => (
                <TC
                  key={`entry-${label}`}
                  sx={{
                    ...headerCellBase,
                    backgroundColor: "#E8F5E9",
                    borderLeft: i === 0 ? "2px solid #2e7d32" : "1px solid #a5d6a7",
                    borderRight: i === 2 ? "2px solid #2e7d32" : "none",
                    cursor: label !== "Gate" ? "pointer" : "default",
                  }}
                  onClick={() => label !== "Gate" && handleSort(`entry${label}`)}
                >
                  {t(label)} {label !== "Gate" && renderSortIcons(`entry${label}`)}
                </TC>
              ))}
              {/* Exit sub-headers */}
              {["Date", "Time", "Gate"].map((label, i) => (
                <TC
                  key={`exit-${label}`}
                  sx={{
                    ...headerCellBase,
                    backgroundColor: "#FFEBEE",
                    borderLeft: i === 0 ? "2px solid #da190b" : "1px solid #ef9a9a",
                    borderRight: i === 2 ? "2px solid #da190b" : "none",
                    cursor: label !== "Gate" ? "pointer" : "default",
                  }}
                  onClick={() => label !== "Gate" && handleSort(`exit${label}`)}
                >
                  {t(label)} {label !== "Gate" && renderSortIcons(`exit${label}`)}
                </TC>
              ))}
              <TC sx={{ backgroundColor: "#647490", borderBottom: "none" }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array(skeletonRowCount).fill(0).map((_, i) => (
                <TableRow key={i} sx={{ height: 50 }}>
                  {Array(selectable ? 11 : 10).fill(0).map((__, j) => (
                    <TC key={j}><Skeleton variant="text" width="70%" /></TC>
                  ))}
                </TableRow>
              ))
            ) : sortedData.length === 0 ? (
              <TableRow>
                <TC colSpan={selectable ? 11 : 10} sx={{ textAlign: "center", height: 100, color: "#aaa" }}>
                  {t("noDataAvailable")}
                </TC>
              </TableRow>
            ) : (
              sortedData.map((row, rowIndex) => (
                <React.Fragment key={row.id ?? rowIndex}>
                  <TableRow
                    sx={{
                      height: "50px",
                      maxHeight: "50px",
                      cursor: "pointer",
                      "&:nth-of-type(odd)": {
                        backgroundColor: expandedRow === rowIndex ? "#e3f2fd" : "#f5f5f5",
                      },
                      "&:nth-of-type(even)": {
                        backgroundColor: expandedRow === rowIndex ? "#e3f2fd" : "#ffffff",
                      },
                      borderBottom: "1px solid #ddd",
                      "&:hover": { backgroundColor: "#f0f0f0" },
                    }}
                    onClick={() => setExpandedRow(expandedRow === rowIndex ? null : rowIndex)}
                  >
                    {selectable && (
                      <TC
                        sx={{
                          width: "1%",
                          whiteSpace: "nowrap",
                          padding: "4px 8px",
                          textAlign: "center",
                          borderRight: "1px solid #D8DEE8",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedRowKeys.includes(row.id ?? row._logId)}
                          onChange={(e) => handleSelectRow(row.id ?? row._logId, e)}
                          sx={{
                            padding: "2px",
                            color: "#647490",
                            "&.Mui-checked": { color: "#185FA5" },
                          }}
                        />
                      </TC>
                    )}
                    {/* Vehicle column — uses VehicleNameCell with display:flex in styled CSS (not sx) */}
                    <VehicleNameCell align="left">
                      <Box sx={{ width: "20px", flexShrink: 0 }}>
                        {!row.hasExit && <InsideIcon />}
                      </Box>
                      <TableVehicleAvatar src={row.vehicleImage} label={row.vehicleName} />
                      <Tooltip title={row.vehicleName || ""}>
                        <span className="vehicle-name-text">{row.vehicleName}</span>
                      </Tooltip>
                    </VehicleNameCell>
                    <TC>{row.vehicleNumber}</TC>
                    <TC>{row.type}</TC>
                    {/* Entry */}
                    <TC sx={{ textAlign: "center", borderLeft: "2px solid #2e7d32" }}>{row.entryDate}</TC>
                    <TC sx={{ textAlign: "center", borderLeft: "1px solid #a5d6a7" }}>{row.entryTime}</TC>
                    <TC sx={{ textAlign: "center", borderLeft: "1px solid #a5d6a7", borderRight: "2px solid #2e7d32" }}>{row.entryGate}</TC>
                    {/* Exit */}
                    <TC sx={{ textAlign: "center", borderLeft: "2px solid #da190b" }}>{row.exitDate}</TC>
                    <TC sx={{ textAlign: "center", borderLeft: "1px solid #ef9a9a" }}>{row.exitTime}</TC>
                    <TC sx={{ textAlign: "center", borderLeft: "1px solid #ef9a9a", borderRight: "2px solid #da190b" }}>{row.exitGate}</TC>
                    {/* Duration + expand */}
                    <TC>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "4px" }}>
                        <span>{row.duration}</span>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setExpandedRow(expandedRow === rowIndex ? null : rowIndex); }}
                        >
                          {expandedRow === rowIndex ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </Box>
                    </TC>
                  </TableRow>

                  {/* Expanded row */}
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={selectable ? 11 : 10}>
                      <Collapse in={expandedRow === rowIndex} timeout="auto" unmountOnExit>
                        <ExpandedRowContent
                          row={row}
                          gates={gates}
                          onUpdated={onLogUpdated}
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            px: 2,
            py: 1.5,
            borderTop: "1px solid #D8DEE8",
            backgroundColor: "#FFFFFF",
          }}
        >
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => onPageChange?.(currentPage - 1)}
            style={{
              ...paginationButtonBase,
              cursor: currentPage <= 1 ? "not-allowed" : "pointer",
              opacity: currentPage <= 1 ? 0.45 : 1,
            }}
          >
            {"<"}
          </button>
          {paginationPages.map((page) => {
            const isActive = page === currentPage;

            return (
              <button
                key={page}
                type="button"
                onClick={() => onPageChange?.(page)}
                style={{
                  ...paginationButtonBase,
                  border: isActive ? "1px solid #20A8E0" : "1px solid #C8D0DC",
                  cursor: "pointer",
                  background: isActive ? "#20A8E0" : "#FFFFFF",
                  color: isActive ? "#FFFFFF" : "#1F242E",
                  fontWeight: isActive ? 700 : 500,
                }}
              >
                {page}
              </button>
            );
          })}
          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange?.(currentPage + 1)}
            style={{
              ...paginationButtonBase,
              cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
              opacity: currentPage >= totalPages ? 0.45 : 1,
            }}
          >
            {">"}
          </button>
        </Box>
      )}
    </Box>
  );
}
